import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  InputAdornment,
  Avatar,
  Tooltip,
  Switch,
  FormControlLabel,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  Card,
  CardContent,
  Tabs,
  Tab,
  useTheme,
  Stack,
  Zoom,
  Skeleton,
  Fade,
  Grow,
  alpha,
  useMediaQuery,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as ApproveIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  VerifiedUser as VerifiedIcon,
  Pending as PendingIcon,
  Cancel as CancelIcon,
  Visibility as ViewIcon,
  Security as SecurityIcon,
  Star as StarIcon,
  Sort as SortIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import {
  collection,
  query,
  getDocs,
  doc,
  updateDoc,
  deleteDoc,
  orderBy,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';

const UserControl = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [filterTab, setFilterTab] = useState(0);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [roleFilter, setRoleFilter] = useState('all');
  
  const [editForm, setEditForm] = useState({
    displayName: '',
    email: '',
    phone: '',
    address: '',
    role: '',
    isApproved: false,
  });

  const [stats, setStats] = useState({
    total: 0,
    superAdmins: 0,
    admins: 0,
    pending: 0,
    approved: 0,
    rejected: 0,
  });

  useEffect(() => {
    fetchUsers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortBy, sortOrder]);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const usersQuery = query(collection(db, 'users'), orderBy(sortBy, sortOrder));
      const querySnapshot = await getDocs(usersQuery);
      const usersData = [];
      let superAdminCount = 0;
      let adminCount = 0;
      let pendingCount = 0;
      let approvedCount = 0;
      let rejectedCount = 0;

      for (const docSnap of querySnapshot.docs) {
        const userData = docSnap.data();
        const userWithId = { id: docSnap.id, ...userData };
        usersData.push(userWithId);

        if (userData.role === 'superadmin') superAdminCount++;
        if (userData.role === 'admin') adminCount++;
        if (userData.role === 'pending') pendingCount++;
        if (userData.isApproved) approvedCount++;
        if (userData.role === 'rejected') rejectedCount++;
      }

      setUsers(usersData);
      setStats({
        total: usersData.length,
        superAdmins: superAdminCount,
        admins: adminCount,
        pending: pendingCount,
        approved: approvedCount,
        rejected: rejectedCount,
      });
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPage(0);
  };

  const getFilteredAndSortedUsers = () => {
    let filtered = users.filter((user) => {
      const matchesSearch = 
        user.displayName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.phone?.includes(searchTerm);
      
      if (filterTab === 0 && roleFilter !== 'all') {
        return matchesSearch && user.role === roleFilter;
      }
      if (filterTab === 1) return matchesSearch && user.role === 'superadmin';
      if (filterTab === 2) return matchesSearch && user.role === 'admin';
      if (filterTab === 3) return matchesSearch && user.role === 'pending';
      if (filterTab === 4) return matchesSearch && user.isApproved === true && user.role !== 'superadmin';
      if (filterTab === 5) return matchesSearch && user.role === 'rejected';
      
      return matchesSearch;
    });

    filtered.sort((a, b) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];
      
      if (sortBy === 'createdAt') {
        aVal = new Date(aVal).getTime();
        bVal = new Date(bVal).getTime();
      }
      
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return filtered;
  };

  const filteredUsers = getFilteredAndSortedUsers();
  const paginatedUsers = filteredUsers.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const handleOpenDialog = (user, type) => {
    setSelectedUser(user);
    setDialogType(type);
    
    if (type === 'edit') {
      setEditForm({
        displayName: user.displayName || '',
        email: user.email || '',
        phone: user.phone || '',
        address: user.address || '',
        role: user.role || 'user',
        isApproved: user.isApproved || false,
      });
    }
    
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
  };

  const handleUpdateUser = async () => {
    setLoading(true);
    setError('');
    
    try {
      const userRef = doc(db, 'users', selectedUser.id);
      const updates = {
        displayName: editForm.displayName,
        phone: editForm.phone,
        address: editForm.address,
        role: editForm.role,
        isApproved: editForm.isApproved,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser.uid,
      };
      
      await updateDoc(userRef, updates);
      setSuccess('User updated successfully');
      fetchUsers();
      handleCloseDialog();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error updating user:', error);
      setError('Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (selectedUser.role === 'superadmin') {
      setError('Cannot delete Super Admin account');
      return;
    }
    
    if (selectedUser.id === currentUser.uid) {
      setError('You cannot delete your own account');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      await deleteDoc(doc(db, 'users', selectedUser.id));
      setSuccess('User deleted successfully');
      fetchUsers();
      handleCloseDialog();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error deleting user:', error);
      setError('Failed to delete user');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveUser = async (userId, isApproved) => {
    setLoading(true);
    try {
      const userRef = doc(db, 'users', userId);
      const updates = {
        isApproved: isApproved,
        role: isApproved ? 'admin' : 'rejected',
        approvedAt: isApproved ? new Date().toISOString() : null,
        approvedBy: currentUser.uid,
      };
      
      await updateDoc(userRef, updates);
      setSuccess(isApproved ? 'User approved successfully' : 'User rejected');
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error approving user:', error);
      setError('Failed to update user status');
    } finally {
      setLoading(false);
    }
  };

  const handleMakeSuperAdmin = async (userId) => {
    setLoading(true);
    try {
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        role: 'superadmin',
        isApproved: true,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser.uid,
      });
      
      setSuccess('User promoted to Super Admin');
      fetchUsers();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error making super admin:', error);
      setError('Failed to update user role');
    } finally {
      setLoading(false);
    }
  };

  const getRoleChip = (user) => {
    const { role, isApproved } = user;
    
    if (role === 'superadmin') {
      return (
        <Chip 
          label="Super Admin" 
          color="secondary" 
          size="small" 
          icon={<SecurityIcon sx={{ fontSize: 14 }} />}
          sx={{ fontWeight: 600 }}
        />
      );
    } else if (role === 'admin' && isApproved) {
      return <Chip label="Admin" color="primary" size="small" icon={<AdminIcon sx={{ fontSize: 14 }} />} />;
    } else if (role === 'pending') {
      return <Chip label="Pending" color="warning" size="small" icon={<PendingIcon sx={{ fontSize: 14 }} />} />;
    } else if (role === 'rejected') {
      return <Chip label="Rejected" color="error" size="small" icon={<CancelIcon sx={{ fontSize: 14 }} />} />;
    } else if (isApproved) {
      return <Chip label="Approved" color="success" size="small" icon={<VerifiedIcon sx={{ fontSize: 14 }} />} />;
    } else {
      return <Chip label="User" color="default" size="small" icon={<PersonIcon sx={{ fontSize: 14 }} />} />;
    }
  };

  const StatCard = ({ title, value, icon, color }) => (
    <Card sx={{ 
      borderRadius: 2, 
      minWidth: { xs: 140, sm: 160 },
      flexShrink: 0,
    }}>
      <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, fontSize: { xs: 10, sm: 11 } }}>
              {title}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              bgcolor: alpha(color, 0.1),
              borderRadius: 2,
              p: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {React.cloneElement(icon, { sx: { color: color, fontSize: { xs: 20, sm: 28 } } })}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );

  // Mobile User Card Component
  const UserCard = ({ user, index }) => {
    const [expanded, setExpanded] = useState(false);
    
    return (
      <Zoom in style={{ transitionDelay: `${index * 50}ms` }}>
        <Card sx={{ 
          mb: 2, 
          borderRadius: 2,
          overflow: 'hidden',
        }}>
          <CardContent sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar 
                sx={{ 
                  bgcolor: user.role === 'superadmin' ? theme.palette.secondary.main : theme.palette.primary.main,
                  width: 50,
                  height: 50,
                }}
              >
                {user.displayName?.[0] || user.email?.[0]}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>
                    {user.displayName || 'N/A'}
                  </Typography>
                  {user.role === 'superadmin' && (
                    <StarIcon sx={{ fontSize: 14, color: theme.palette.secondary.main }} />
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {user.email}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  {getRoleChip(user)}
                </Box>
              </Box>
              <IconButton onClick={() => setExpanded(!expanded)} size="small">
                {expanded ? <CloseIcon /> : <ViewIcon />}
              </IconButton>
            </Stack>
            
            {expanded && (
              <Fade in={expanded}>
                <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Stack spacing={1.5}>
                    {user.phone && (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">{user.phone}</Typography>
                      </Stack>
                    )}
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2">{user.email}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Typography variant="caption" color="text.secondary">
                        Joined: {new Date(user.createdAt).toLocaleDateString()}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Tooltip title="Edit User">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(user, 'edit')}
                            sx={{ color: theme.palette.primary.main }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {user.role === 'pending' && (
                          <>
                            <Tooltip title="Approve">
                              <IconButton
                                size="small"
                                onClick={() => handleApproveUser(user.id, true)}
                                sx={{ color: theme.palette.success.main }}
                              >
                                <ApproveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reject">
                              <IconButton
                                size="small"
                                onClick={() => handleApproveUser(user.id, false)}
                                sx={{ color: theme.palette.error.main }}
                              >
                                <BlockIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                        {user.role !== 'superadmin' && (
                          <Tooltip title="Make Super Admin">
                            <IconButton
                              size="small"
                              onClick={() => handleMakeSuperAdmin(user.id)}
                              sx={{ color: theme.palette.secondary.main }}
                            >
                              <SecurityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {user.id !== currentUser?.uid && user.role !== 'superadmin' && (
                          <Tooltip title="Delete User">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenDialog(user, 'delete')}
                              sx={{ color: theme.palette.error.main }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Stack>
                    </Stack>
                  </Stack>
                </Box>
              </Fade>
            )}
          </CardContent>
        </Card>
      </Zoom>
    );
  };

  return (
    <Box sx={{ pb: { xs: 2, sm: 3 } }}>
 

      {/* Success/Error Messages */}
      <Fade in={!!success || !!error}>
        <Stack spacing={2} sx={{ mb: 3 }}>
          {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ borderRadius: 2 }}>{success}</Alert>}
          {error && <Alert severity="error" onClose={() => setError('')} sx={{ borderRadius: 2 }}>{error}</Alert>}
        </Stack>
      </Fade>

      {/* Stats Cards - Horizontal Scroll on Mobile */}
      <Box sx={{ 
        overflowX: 'auto', 
        mb: 4,
        pb: 1,
        '&::-webkit-scrollbar': {
          height: 4,
        },
        '&::-webkit-scrollbar-track': {
          bgcolor: 'grey.100',
          borderRadius: 2,
        },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: 'grey.400',
          borderRadius: 2,
        },
      }}>
        <Stack direction="row" spacing={2} sx={{ minWidth: 'min-content' }}>
          <StatCard title="Total Users" value={stats.total} icon={<PersonIcon />} color={theme.palette.primary.main} />
          <StatCard title="Super Admins" value={stats.superAdmins} icon={<SecurityIcon />} color={theme.palette.secondary.main} />
          <StatCard title="Admins" value={stats.admins} icon={<AdminIcon />} color={theme.palette.info.main} />
          <StatCard title="Pending" value={stats.pending} icon={<PendingIcon />} color={theme.palette.warning.main} />
          <StatCard title="Approved" value={stats.approved} icon={<VerifiedIcon />} color={theme.palette.success.main} />
          <StatCard title="Rejected" value={stats.rejected} icon={<CancelIcon />} color={theme.palette.error.main} />
        </Stack>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: { xs: 2, sm: 3 }, mb: 3, borderRadius: 2 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={handleSearch}
              size={isMobile ? "small" : "medium"}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1 }}
            />
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={() => setShowFilters(!showFilters)}
                sx={{ borderRadius: 2, px: { xs: 1.5, sm: 2 } }}
                size={isMobile ? "small" : "medium"}
              >
                {!isMobile && "Filters"}
              </Button>
              <Button
                variant="outlined"
                startIcon={<SortIcon />}
                onClick={() => handleSort('createdAt')}
                sx={{ borderRadius: 2, px: { xs: 1.5, sm: 2 } }}
                size={isMobile ? "small" : "medium"}
              >
                {!isMobile && "Sort"}
              </Button>
              <IconButton onClick={fetchUsers} sx={{ borderRadius: 2 }} size={isMobile ? "small" : "medium"}>
                <RefreshIcon />
              </IconButton>
            </Stack>
          </Stack>

          {showFilters && (
            <Grow in={showFilters}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <FormControl size="small" sx={{ minWidth: { xs: '100%', sm: 150 } }}>
                  <InputLabel>Role Filter</InputLabel>
                  <Select
                    value={roleFilter}
                    label="Role Filter"
                    onChange={(e) => setRoleFilter(e.target.value)}
                  >
                    <MenuItem value="all">All Roles</MenuItem>
                    <MenuItem value="superadmin">Super Admin</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="user">User</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="text"
                  onClick={() => {
                    setRoleFilter('all');
                    setSearchTerm('');
                    setFilterTab(0);
                  }}
                >
                  Clear Filters
                </Button>
              </Stack>
            </Grow>
          )}
        </Stack>

        <Tabs 
          value={filterTab} 
          onChange={(e, v) => setFilterTab(v)} 
          sx={{ mt: 2, borderBottom: 1, borderColor: 'divider', minHeight: { xs: 36, sm: 48 } }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label="All" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, minHeight: { xs: 36, sm: 48 } }} />
          <Tab label="Super Admins" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, minHeight: { xs: 36, sm: 48 } }} />
          <Tab label="Admins" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, minHeight: { xs: 36, sm: 48 } }} />
          <Tab label="Pending" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, minHeight: { xs: 36, sm: 48 } }} />
          <Tab label="Approved" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, minHeight: { xs: 36, sm: 48 } }} />
          <Tab label="Rejected" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, minHeight: { xs: 36, sm: 48 } }} />
        </Tabs>
      </Paper>

      {/* Users Table/Cards */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ p: 3 }}>
            <Stack spacing={2}>
              {[1, 2, 3, 4, 5].map((_, idx) => (
                <Skeleton key={idx} variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
              ))}
            </Stack>
          </Box>
        ) : paginatedUsers.length === 0 ? (
          <Box sx={{ p: 8, textAlign: 'center' }}>
            <Typography color="text.secondary">No users found</Typography>
          </Box>
        ) : (
          <>
            {/* Desktop Table View */}
            {!isMobile && (
              <Box sx={{ overflowX: 'auto' }}>
                <Table sx={{ minWidth: 800 }}>
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                      <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Contact</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Joined</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedUsers.map((user, index) => (
                      <TableRow key={user.id} hover>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Avatar 
                              sx={{ 
                                bgcolor: user.role === 'superadmin' ? theme.palette.secondary.main : theme.palette.primary.main,
                                width: 40,
                                height: 40,
                              }}
                            >
                              {user.displayName?.[0] || user.email?.[0]}
                            </Avatar>
                            <Box>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                  {user.displayName || 'N/A'}
                                </Typography>
                                {user.role === 'superadmin' && (
                                  <StarIcon sx={{ fontSize: 14, color: theme.palette.secondary.main }} />
                                )}
                              </Stack>
                              <Typography variant="caption" color="text.secondary">
                                {user.email}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {user.phone && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PhoneIcon sx={{ fontSize: 12 }} /> {user.phone}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>{getRoleChip(user)}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(user.createdAt).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <Tooltip title="Edit User">
                              <IconButton size="small" onClick={() => handleOpenDialog(user, 'edit')} sx={{ color: theme.palette.primary.main }}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {user.role === 'pending' && (
                              <>
                                <Tooltip title="Approve">
                                  <IconButton size="small" onClick={() => handleApproveUser(user.id, true)} sx={{ color: theme.palette.success.main }}>
                                    <ApproveIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Reject">
                                  <IconButton size="small" onClick={() => handleApproveUser(user.id, false)} sx={{ color: theme.palette.error.main }}>
                                    <BlockIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            )}
                            {user.role !== 'superadmin' && (
                              <Tooltip title="Make Super Admin">
                                <IconButton size="small" onClick={() => handleMakeSuperAdmin(user.id)} sx={{ color: theme.palette.secondary.main }}>
                                  <SecurityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {user.id !== currentUser?.uid && user.role !== 'superadmin' && (
                              <Tooltip title="Delete User">
                                <IconButton size="small" onClick={() => handleOpenDialog(user, 'delete')} sx={{ color: theme.palette.error.main }}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            {/* Mobile Card View */}
            {isMobile && (
              <Box sx={{ p: 2 }}>
                {paginatedUsers.map((user, index) => (
                  <UserCard key={user.id} user={user} index={index} />
                ))}
              </Box>
            )}

            <TablePagination
              rowsPerPageOptions={isMobile ? [5, 10, 25] : [5, 10, 25, 50]}
              component="div"
              count={filteredUsers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      {/* Edit User Dialog */}
      <Dialog open={dialogOpen && dialogType === 'edit'} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {selectedUser && (
            <Stack spacing={2}>
              <TextField
                fullWidth
                label="Display Name"
                value={editForm.displayName}
                onChange={(e) => setEditForm({ ...editForm, displayName: e.target.value })}
                size="small"
              />
              <TextField
                fullWidth
                label="Email"
                value={editForm.email}
                disabled
                size="small"
              />
              <TextField
                fullWidth
                label="Phone"
                value={editForm.phone}
                onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                size="small"
              />
              <TextField
                fullWidth
                label="Address"
                multiline
                rows={2}
                value={editForm.address}
                onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                size="small"
              />
              <FormControl fullWidth size="small">
                <InputLabel>Role</InputLabel>
                <Select
                  value={editForm.role}
                  label="Role"
                  onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                >
                  <MenuItem value="user">User</MenuItem>
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="superadmin">Super Admin</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.isApproved}
                    onChange={(e) => setEditForm({ ...editForm, isApproved: e.target.checked })}
                  />
                }
                label="Approved Status"
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleUpdateUser} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete User Dialog */}
      <Dialog open={dialogOpen && dialogType === 'delete'} onClose={handleCloseDialog}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete user <strong>{selectedUser?.displayName || selectedUser?.email}</strong>?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UserControl;